import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMField } from "../PMField";
import { PMDialog } from "../PMDialog";
import { useDispatch, useSelector } from "react-redux";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";
import {
  createDepartmentAsync,
  getDepartmentsAsync,
} from "../../../slices/department";

const FETCH_URL = "/departments/";

export const DepartmentSelector = (props) => {
  const {
    setFieldValue,
    values,
    onChange,
    errors,
    module,
    module_param,
    fields,
    noLabel,
  } = props;

  const name = props?.name ?? "department_id";
  const label = props?.label ?? "Department";

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const itemState = useSelector((store) => store.departments);

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [inCreateView, setInCreateView] = useState(false);

  // ── Search & list state ───────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open && !inCreateView);

  // ── Selected state ────────────────────────────────────────────────────────
  const [selected, setSelected] = useState({
    id: null,
    field1: "Ingen valgt",
  });

  useEffect(() => {
    if (props?.selected?.id) {
      setSelected({
        id: props.selected.id,
        field1: props.selected.displayTitle ?? "Ingen valgt",
      });
    }
  }, [props.selected]);

  // ── Map Redux state to list items ─────────────────────────────────────────
  useEffect(() => {
    const array = [
      { id: null, value: null, primary: "Ingen valgt", secondary: null },
    ];
    itemState?.data?.forEach((row) => {
      array.push({
        id: row.id,
        value: row.id,
        primary: row.displayTitle ?? "",
        secondary: null,
        object: row,
      });
    });
    setItems(array);
    setLoading(false);
  }, [itemState]);

  // ── Open/close ────────────────────────────────────────────────────────────
  const handleOpen = () => {
    if (props.disabled) return;
    setOpen(true);
    setInCreateView(false);
  };

  const handleClose = () => {
    setOpen(false);
    setInCreateView(false);
    setQuery("");
  };

  // ── Propagate selection ───────────────────────────────────────────────────
  const handleSelect = (item) => {
    setSelected({ id: item.id, field1: item.primary ?? "Ingen valgt" });

    if (setFieldValue) setFieldValue(name, item.id);
    if (onChange) onChange({ target: { name, value: item.id } });
    handleClose();
  };

  const handleSelectFromForm = (data) => {
    setSelected({ id: data?.id, field1: data?.displayTitle ?? "Ingen valgt" });
    if (values) setFieldValue(name, data?.id);
    else onChange({ target: { name, value: data?.id } });
    handleClose();
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchItems = useCallback(
    (searchQuery) => {
      setLoading(true);
      dispatch(
        getDepartmentsAsync({
          query: searchQuery || "",
          page: 1,
          per_page: 20,
          sortBy: "id",
          sort: "desc",
          fields: "id,displayTitle",
        })
      );
    },
    [dispatch]
  );

  const debouncedFetch = useCallback(
    debounce((q) => fetchItems(q), 300),
    [fetchItems]
  );

  useEffect(() => {
    if (!open) return;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, debouncedFetch]);

  // ── Create form ───────────────────────────────────────────────────────────
  const createOrUpdate = (vals) => {
    const data = { ...vals, customer_id: module_param };
    return dispatch(createDepartmentAsync(data));
  };

  const renderCreateForm = () => (
    <Formik
      initialValues={{
        id: null,
        title: fields?.title ?? "",
        number: fields?.number ?? "",
        submit: null,
      }}
      validationSchema={Yup.object({
        title: Yup.string().required(t("Title is required")),
      })}
      onSubmit={(vals, helpers) => {
        try {
          toast.promise(createOrUpdate(vals), {
            loading: t("Saving...."),
            success: (result) => {
              const payload = result?.payload ?? result?.data ?? result;
              if (!payload?.id) throw new Error(payload?.errors ?? "Error");
              fetchItems(query);
              if (payload?.id) handleSelectFromForm(payload);
              return t("Data was saved!");
            },
            error: () => t("Something went wrong. Could not save data!"),
          });
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        } catch (err) {
          toast.error("Something went wrong...");
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <Form className="space-y-4 p-4">
          <PMField name="number" type="number" label={t("Number")} {...formik} />
          <PMField name="title" type="text" label={t("Title")} {...formik} />
          <div className="pt-2">
            <button
              type="button"
              onClick={formik.submitForm}
              disabled={formik.isSubmitting}
              className="w-full inline-flex justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {t("create")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  // ── Trigger button ────────────────────────────────────────────────────────
  const hasSelection = selected?.id != null;

  const triggerButton = (
    <div>
      {noLabel !== false && (
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t(label)}
      </label>
      )}
      <button
        type="button"
        disabled={props.disabled}
        onClick={handleOpen}
        className={[
          "relative w-full flex items-center justify-between",
          "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
          "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
          props.disabled
            ? "cursor-not-allowed border-gray-200 text-gray-400"
            : hasSelection
            ? "border-blue-400 text-gray-900 hover:border-blue-500"
            : "border-gray-300 text-gray-500 hover:border-gray-400",
        ].join(" ")}
      >
        <span className="truncate">{selected.field1}</span>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
      </button>
      {errors?.[name] && (
        <p className="mt-1 text-xs text-red-600">{errors[name]}</p>
      )}
    </div>
  );

  // ── Dialog content ────────────────────────────────────────────────────────
  const dialogContent = (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          {inCreateView ? (
            <button
              type="button"
              onClick={() => setInCreateView(false)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Tilbake
            </button>
          ) : (
            <h3 className="text-base font-semibold text-gray-900">
              {t(label)}
            </h3>
          )}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {!inCreateView && (
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søk etter avdeling..."
              autoFocus
              className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {inCreateView ? (
          renderCreateForm()
        ) : loading || itemState?.loading ? (
          <SelectorListSkeleton />
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((item) => {
              const isSelected = item.id === selected?.id;
              return (
                <li
                  key={item.id ?? "none"}
                  onClick={() => handleSelect(item)}
                  className={[
                    "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                    isSelected
                      ? "bg-blue-50 text-blue-700"
                      : "hover:bg-gray-50 text-gray-900",
                  ].join(" ")}
                >
                  <p className="text-sm font-medium truncate">{item.primary}</p>
                  {isSelected && (
                    <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      {!inCreateView && (
        <div className="flex-shrink-0 border-t border-gray-200 p-3">
          <button
            type="button"
            onClick={() => setInCreateView(true)}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Opprett ny avdeling
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {triggerButton}
      <PMDialog
        open={open}
        onClose={handleClose}
        size="md"
        dense
        zIndex={props.zIndex}
        closeOnOutsideClick
      >
        {dialogContent}
      </PMDialog>
    </>
  );
};
